/**
 * Subscription API client
 */

import { apiClient } from './client'

export interface SubscriptionInfo {
  subscription_type: string
  plan_name: string
  monthly_credits: number
  price_usd?: number
  next_billing_date?: string
  is_active: boolean
  features: Record<string, any>
  is_trial_active?: boolean
  trial_end_date?: string
  trial_used?: boolean
}

export interface SubscriptionPlan {
  name: string
  monthly_credits: number
  price_usd?: number
  features: Record<string, any>
  max_documents?: number
  max_editors?: number
  latex_time_limit?: number | null
}

export interface CardDetails {
  cardholder_name: string
  card_number: string
  expiry_date: string // MM/YY
  cvv: string
  billing_address_line1?: string
  billing_address_line2?: string
  billing_city?: string
  billing_state?: string
  billing_postal_code?: string
  billing_country?: string
}

export interface CreateSubscriptionRequest {
  subscription_type: 'student' | 'researcher'
  card_details?: CardDetails
  payment_method_id?: string
  transaction_id?: string
  amount_paid?: number
}

export interface SubscriptionHistoryEntry {
  id: string
  subscription_type: string
  start_date: string
  end_date?: string
  amount_paid?: number
  transaction_id?: string
  status: string
}

export const subscriptionsAPI = {
  async getInfo(): Promise<SubscriptionInfo> {
    return apiClient.get('/api/subscriptions/info')
  },

  async getPlans(): Promise<Record<string, SubscriptionPlan>> {
    const response = await apiClient.get<{ plans: Record<string, SubscriptionPlan }>('/api/subscriptions/plans')
    return response.plans
  },

  async createSubscription(request: CreateSubscriptionRequest): Promise<{
    success: boolean
    subscription_type: string
    next_billing_date: string
    monthly_credits_added: number
  }> {
    return apiClient.post('/api/subscriptions/create', request)
  },

  async cancelSubscription(): Promise<{
    success: boolean
    message: string
  }> {
    return apiClient.post('/api/subscriptions/cancel')
  },

  async getHistory(): Promise<SubscriptionHistoryEntry[]> {
    const response = await apiClient.get<{ history: SubscriptionHistoryEntry[] }>('/api/subscriptions/history')
    return response.history
  },

  async startTrial(): Promise<{
    success: boolean
    trial_start_date: string
    trial_end_date: string
    trial_duration_days: number
  }> {
    return apiClient.post('/api/subscriptions/start-trial', {})
  },
}

